import { useSelector } from 'react-redux';
import { CURRENT_DEFAULT_MODEL } from '../../constants';
import { addOptionalBoolField, addOptionalField } from '../../utils/application';

export const AdvancedSettingsFields = {
  model: 'model',

  maxAnswerLength: 'maxAnswerLength',
  maxContextLength: 'maxContextLength',
  maxContextChunks: 'maxContextChunks',

  seeContext: 'seeContext',
  smoothingToggle: 'enableSmoothing',

  enableTextSearch: 'enableTextSearch',
  enableMath: 'enableMath',
  enableJS: 'enableJS',
  useFYEContext: 'useFYEContext',

  version: 'version',
  numOfPages: 'numOfPages',

  includeInferredMetadata: 'includeInferredMetadata',

  seed: 'seed',
  temperature: 'temperature',
  top_p: 'topP',

  includeUserDocsToggle: 'includeUserDocs',

  // non API settings
  overrideQuestion: 'overrideQuestion',

  dailySummariesToggle: 'dailySummariesToggle',
  headlinesToggle: 'enableHeadlines',
  followUpQuestionsToggle: 'enableFollowUpQuestions',
};

export function addAdvancedSettingValues(values, form, skipValues = []) {
  const neverIncludeValues = [
    AdvancedSettingsFields.dailySummariesToggle,
    AdvancedSettingsFields.headlinesToggle,
    AdvancedSettingsFields.overrideQuestion,
    AdvancedSettingsFields.followUpQuestionsToggle,
    ...skipValues,
  ];
  const entries = Object.entries(values);
  entries.forEach(([keyname, value]) => {
    const shouldSkip = neverIncludeValues.includes(keyname);
    const metadata = getAdvancedSettingAPIMetadata(keyname);

    if (shouldSkip || !metadata) {
      // do nothing
    } else {
      const { keyName, valueType } = metadata;
      switch (valueType) {
        case ADVANCED_SETTING_VALUE_TYPES.string: {
          addOptionalField(keyName, value, form);
          break;
        }
        case ADVANCED_SETTING_VALUE_TYPES.integer: {
          addOptionalField(keyName, value, form);
          break;
        }
        case ADVANCED_SETTING_VALUE_TYPES.float: {
          addOptionalField(keyName, value, form, true);
          break;
        }
        case ADVANCED_SETTING_VALUE_TYPES.bool: {
          addOptionalBoolField(keyName, value, form);
          break;
        }
        default:
          break;
      }
    }
  });

  return form;
}

export const ADVANCED_SETTING_VALUE_TYPES = {
  string: 'string',
  bool: 'bool',
  integer: 'integer',
  float: 'float',
};

function assignMetadata(value, keyName, valueType, formDisplayName) {
  return {
    defaultValue: value,
    keyName,
    valueType,
    formDisplayName,
  };
}

export function getAdvancedSettingAPIMetadata(name) {
  const defaults = {
    [AdvancedSettingsFields.model]: assignMetadata(
      CURRENT_DEFAULT_MODEL,
      'model',
      ADVANCED_SETTING_VALUE_TYPES.string,
      'Model'
    ),
    [AdvancedSettingsFields.version]: assignMetadata(
      2,
      'crawl_version',
      ADVANCED_SETTING_VALUE_TYPES.integer,
      'Version'
    ),

    [AdvancedSettingsFields.enableJS]: assignMetadata(true, 'run_js', ADVANCED_SETTING_VALUE_TYPES.bool, 'Enable JS'),
    [AdvancedSettingsFields.enableTextSearch]: assignMetadata(
      false,
      'enable_text_search',
      ADVANCED_SETTING_VALUE_TYPES.bool,
      'Enable Text Search'
    ),
    [AdvancedSettingsFields.enableMath]: assignMetadata(
      true,
      'enable_math_correction',
      ADVANCED_SETTING_VALUE_TYPES.bool,
      'Enable Refinement'
    ),
    [AdvancedSettingsFields.useFYEContext]: assignMetadata(
      false,
      'use_fye_context',
      ADVANCED_SETTING_VALUE_TYPES.bool,
      'Use FYE Context'
    ),

    [AdvancedSettingsFields.seeContext]: assignMetadata(
      undefined,
      'need_context',
      ADVANCED_SETTING_VALUE_TYPES.bool,
      'Show Context'
    ),
    [AdvancedSettingsFields.maxAnswerLength]: assignMetadata(
      undefined,
      'max_answer_tokens',
      ADVANCED_SETTING_VALUE_TYPES.integer,
      'Max Answer Length'
    ),
    [AdvancedSettingsFields.maxContextLength]: assignMetadata(
      undefined,
      'max_context_tokens',
      ADVANCED_SETTING_VALUE_TYPES.integer,
      'Max Context Length'
    ),
    [AdvancedSettingsFields.maxContextChunks]: assignMetadata(
      undefined,
      'max_context_chunks',
      ADVANCED_SETTING_VALUE_TYPES.integer,
      'Max Context Chunks'
    ),
    [AdvancedSettingsFields.smoothingToggle]: assignMetadata(
      false,
      'enable_smoothing',
      ADVANCED_SETTING_VALUE_TYPES.bool,
      'Smoothing Toggle'
    ),

    [AdvancedSettingsFields.includeInferredMetadata]: assignMetadata(
      false,
      'use_inferred_metadata',
      ADVANCED_SETTING_VALUE_TYPES.bool,
      'Include Inferred Metadata'
    ),
    [AdvancedSettingsFields.followUpQuestionsToggle]: assignMetadata(
      undefined,
      'generate_follow_ups',
      ADVANCED_SETTING_VALUE_TYPES.bool,
      'Follow Up Questions'
    ),
    [AdvancedSettingsFields.includeUserDocsToggle]: assignMetadata(
      undefined,
      'include_user_docs',
      ADVANCED_SETTING_VALUE_TYPES.bool,
      'Include My Uploaded Documents'
    ),
    [AdvancedSettingsFields.numOfPages]: assignMetadata(
      undefined,
      'max_pages',
      ADVANCED_SETTING_VALUE_TYPES.integer,
      'Number of Pages'
    ),

    [AdvancedSettingsFields.seed]: assignMetadata(undefined, 'seed', ADVANCED_SETTING_VALUE_TYPES.integer, 'Seed'),
    [AdvancedSettingsFields.temperature]: assignMetadata(
      undefined,
      'temperature',
      ADVANCED_SETTING_VALUE_TYPES.float,
      'Temperature'
    ),
    [AdvancedSettingsFields.top_p]: assignMetadata(undefined, 'top_p', ADVANCED_SETTING_VALUE_TYPES.float, 'Top P'),

    [AdvancedSettingsFields.overrideQuestion]: assignMetadata(
      undefined,
      '',
      ADVANCED_SETTING_VALUE_TYPES.string,
      'Override Question'
    ),
    [AdvancedSettingsFields.dailySummariesToggle]: assignMetadata(
      undefined,
      '',
      ADVANCED_SETTING_VALUE_TYPES.bool,
      'Daily Summaries'
    ),
    [AdvancedSettingsFields.headlinesToggle]: assignMetadata(
      undefined,
      '',
      ADVANCED_SETTING_VALUE_TYPES.bool,
      'Show Headlines'
    ),
  };

  return defaults[name] || null;
}

export function getAdvancedSettingDefaultValue(name) {
  const metadata = getAdvancedSettingAPIMetadata(name);
  return metadata?.defaultValue || null;
}

export function getAdvancedSettingType(name) {
  const metadata = getAdvancedSettingAPIMetadata(name);
  return metadata?.valueType || null;
}

export function toAdvancedSettingDisplayName(name) {
  const metadata = getAdvancedSettingAPIMetadata(name);
  return metadata?.formDisplayName || null;
}

export function getAdvancedSettingDisplayName(name) {
  const metadata = getAdvancedSettingAPIMetadata(name);
  return metadata?.formDisplayName || null;
}

export function useAdvancedSettingsFields() {
  return useSelector((state) => state.advancedSettings.advancedSettingsFields);
}

export function useShowAdvancedSettings() {
  const fields = useAdvancedSettingsFields();
  return fields?.length > 0;
}

export function useAdvancedSettings() {
  const state = useSelector((state) => state.advancedSettings.advancedSettingsValues);
  return state;
}
